import { z } from "zod";

/**
 * Why a Vibe build or deployment failed, as a structured code the PLATFORM
 * mints — the control plane for the failures it authors, an executor (the
 * tenant agent, the build runner) for the ones only it can observe.
 *
 * This is the ONLY input a surface may classify a failure on. `errorReason`
 * is the human-readable detail and is never matched for a verdict: on the
 * build path it is the runner's raw output, which carries the customer's own
 * build log, so any phrase the platform trusts in it can be printed by a
 * `RUN echo` in their Dockerfile. A code cannot be.
 *
 * Stored as TEXT (`VibeBuildJob.reasonCode`, `VibeDeployment.reasonCode`)
 * with an UPPER_SNAKE CHECK, never a Postgres enum: executors deploy
 * independently of the schema, and an enum would turn an unfamiliar code into
 * a failed terminal write. So a reader always goes through
 * `readVibeFailureReasonCode`, which turns a code this build does not know
 * into `null` — and `null` classifies as the generic, red "failed" card,
 * never as anything benign.
 *
 * Adding a code: add it here, then every `Record<VibeFailureReasonCode, …>`
 * (the console's card catalog) refuses to compile until it is given a card.
 */
export const VIBE_FAILURE_REASON_CODES = [
  // ── Build phase, control plane ───────────────────────────────────────────
  "BUILD_DISPATCH_FAILED",
  // The build had compute and ran past the run budget (`VIBE_BUILD_RUN_BUDGET_MS`).
  "BUILD_TIMED_OUT",
  // The executor accepted the build and never gave it compute within the
  // placement budget (`VIBE_BUILD_PLACEMENT_BUDGET_MS`): a capacity wait, never
  // a build that ran too long.
  "BUILD_PLACEMENT_TIMED_OUT",
  // The executor stopped renewing the build's lease on its last attempt.
  "BUILD_LEASE_EXPIRED",
  // ── Build phase, executor ────────────────────────────────────────────────
  "BUILD_NODE_LOST",
  // The executor holds no runnable build for the attempt the agent was told it runs.
  "BUILD_LOST_BY_EXECUTOR",
  // An executor's own terminal verdicts (CodeBuild's build status, Nomad's
  // task state), each naming a structured status — never a phrase in its log.
  "BUILD_EXECUTOR_FAULT",
  "BUILD_EXECUTOR_QUEUE_TIMED_OUT",
  "BUILD_EXECUTOR_TIMED_OUT",
  "BUILD_EXECUTOR_STOPPED",
  "BUILD_EXECUTOR_FAILED",
  "IMAGE_PUSH_DENIED",
  "LEGACY_DOCKER_BUILDER",
  "GIT_CREDENTIAL_REFUSED",
  "GIT_REPOSITORY_NOT_FOUND",
  "GIT_HOST_REFUSED_COMMIT",
  "GIT_HOST_UNREACHABLE",
  "SUBMODULE_FETCH_FAILED",
  "SUBMODULE_FETCH_TIMED_OUT",
  "GIT_LFS_UNAVAILABLE",
  "COMMIT_NOT_IN_REPOSITORY",
  "COMMIT_AMBIGUOUS",
  "NPM_PACKAGE_NOT_FOUND",
  "BUILD_HEAP_OUT_OF_MEMORY",
  // The kernel's OOM killer stopped the failed build step: BuildKit read it off
  // the step's own cgroup, or the step died of SIGKILL while the machine's
  // OOM-kill counter rose (`build_failure_code` in the builder's build-step.sh).
  // A SIGKILL nothing ties to memory is BUILD_STEP_FAILED. Distinct from the
  // heap code: there Node stopped itself at its own ceiling; here the machine
  // ran out.
  "BUILD_STEP_OOM_KILLED",
  "BUILD_STEP_FAILED",
  "IMAGE_TAG_UNREADABLE",
  "BUILD_RUNNER_FAILED",
  // The builder's egress refusals (egress-policy.sh).
  "SUBMODULE_PRIVATE_ADDRESS",
  "SUBMODULE_URL_NOT_FETCHABLE",
  "SUBMODULE_HOST_UNRESOLVED",
  "GIT_LFS_FOREIGN_SERVER",
  "GIT_HOST_ADDRESS_REFUSED",
  "BUILDER_GIT_TOO_OLD",
  // A build whose builder came from its environment's mirror takes EVERY image
  // it pulls from that environment's registry (`docker-build.sh`'s source
  // policy), so a base image resolves through the registry's pull-through cache
  // or not at all. The three ways that can fail, each read off the build
  // CLI's own closing verdict line, which no build step can print:
  //   - the image is on a registry the cache does not serve (a `FROM gcr.io/…`);
  //   - the registry the cache serves has no such image;
  //   - the cache itself refused or could not answer.
  "BASE_IMAGE_REGISTRY_REFUSED",
  "BASE_IMAGE_NOT_FOUND",
  "BASE_IMAGE_CACHE_UNAVAILABLE",
  // ── Deploy phase, control plane ──────────────────────────────────────────
  "DEPLOY_DISPATCH_FAILED",
  "DEPLOY_TIMED_OUT",
  "DEPLOY_FAILED_TO_START",
  "DEPLOY_HEALTH_CHECK_FAILED",
  "DEPLOY_CRASH_LOOPED_DURING_ROLLOUT",
  "DEPLOY_CRASH_LOOPED_WHILE_SERVING_RESTORED",
  "DEPLOY_CRASH_LOOPED_WHILE_SERVING_APP_DOWN",
  "DEPLOY_SMOKE_FAILED_RESTORED",
  "DEPLOY_SMOKE_FAILED_APP_DOWN",
  "IMAGE_PULL_AUTH_FAILED",
  "IMAGE_PULL_AUTH_FAILED_APP_DOWN",
  "COST_SAFETY_ROLLBACK"
] as const;

export type VibeFailureReasonCode = (typeof VIBE_FAILURE_REASON_CODES)[number];

export const VibeFailureReasonCodeSchema = z.enum(VIBE_FAILURE_REASON_CODES);

/**
 * What the tenant agent may say about WHY a deployment's container never ran,
 * separately from the lifecycle verdict it reports as `status`. Only the
 * agent can observe it (it reads the container runtime's own start-failure
 * event); only the control plane knows whether anything was left serving, so
 * the control plane turns it into a deployment code
 * (`IMAGE_PULL_AUTH_FAILED` or `IMAGE_PULL_AUTH_FAILED_APP_DOWN`).
 */
export const VIBE_AGENT_DEPLOYMENT_FAULTS = ["IMAGE_PULL_AUTH"] as const;

export type VibeAgentDeploymentFault = (typeof VIBE_AGENT_DEPLOYMENT_FAULTS)[number];

export const VibeAgentDeploymentFaultSchema = z.enum(VIBE_AGENT_DEPLOYMENT_FAULTS);

const CODES: ReadonlySet<string> = new Set(VIBE_FAILURE_REASON_CODES);

export function isVibeFailureReasonCode(value: unknown): value is VibeFailureReasonCode {
  return typeof value === "string" && CODES.has(value);
}

/**
 * Read a stored or wire code. A code this build does not know — minted by a
 * newer executor, or a malformed value — reads as `null`, the same as "no code
 * recorded", so an unfamiliar code can never borrow a card it was not given.
 */
export function readVibeFailureReasonCode(value: unknown): VibeFailureReasonCode | null {
  return isVibeFailureReasonCode(value) ? value : null;
}

/**
 * The wire form on every response DTO: lenient, never a parse failure. A
 * backend one release behind omits the key, and a newer one may send a code
 * this console does not know yet — both read as `null` rather than failing
 * the whole response.
 */
export const VibeFailureReasonCodeReadSchema = z
  .unknown()
  .transform((value): VibeFailureReasonCode | null => readVibeFailureReasonCode(value));
